import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
	Badge,
	Spinner,
	PlatformMark
} from '../ui';
import { Dropdown, DropdownItem, DropdownSeparator } from '../ui';
import { useAccountRefresh } from '../features/clients';
import { useClientDeletion } from '../features/clients/useClientDeletion';
import { useUser } from '../../contexts/UserContext';
import { canDeleteClientData } from '../../utils/permissions';
import { PATHS, ROUTE_PATTERNS, buildRoute } from '../../constants/Paths';
import { positionMeta } from '../../constants/standing';
import { riskLabel, riskBand } from '../../constants/risk';
import { STANCE_META } from '../../constants/sentiment';
import { relativeTime } from '../../utils/dates';
import { cn } from '../../utils/cn';

/**
 * One account on the client page: who they are, where they stand, and why.
 *
 * The card used to show Followers / Risk / Last scan - three numbers with no
 * verdict. It now leads with the account's position (exposure x standing,
 * computed and stored by the backend), shows each axis as the thing it
 * measures, and carries the saved one-paragraph read, clamped to two lines
 * with the rest a click away. "Risk" is now "Exposure": it is what they post
 * could cost them, not the whole of their reputation.
 */
export function AccountCard({ account, clientId, formatDate, formatNumber, onRefreshComplete }) {
	const navigate = useNavigate();
	const { refreshAccount, isRefreshing } = useAccountRefresh();
	const [expanded, setExpanded] = useState(false);
	const { user } = useUser();
	const { beginDeletion, pendingId } = useClientDeletion();

	const handleRefresh = async () => {
		try {
			await refreshAccount(account._id);
			if (onRefreshComplete) onRefreshComplete();
		} catch (error) {
			console.error('Error refreshing account:', error);
		}
	};

	const handleScanAccount = () => {
		navigate(PATHS.NEW_SCANS, { state: { accountId: account._id, clientId } });
	};

	const isAccountRefreshing = isRefreshing(account._id);
	const s = account.standing;
	const meta = positionMeta(s?.position);
	const scored = account.lastRiskUpdate && typeof account.risk === 'number';
	const band = scored ? riskBand(account.risk) : null;
	const positioned = (s?.for || 0) + (s?.against || 0);
	const againstPct = positioned ? Math.round(((s.against || 0) / positioned) * 100) : null;
	const withPct = positioned ? Math.round(((s.for || 0) / positioned) * 100) : null;

	// Coverage gap is a risk signal in itself: a monitored account nobody has
	// scanned in two weeks is silently unmonitored.
	const last = account.lastScan ? new Date(account.lastScan).getTime() : 0;
	const staleDays = last ? Math.floor((Date.now() - last) / 86400000) : null;
	const isStale = staleDays === null || staleDays > 14;

	const insight = s?.insight?.summary;

	return (
		<Card className="hover:shadow-card-hover transition-shadow">
			<CardContent className="p-3 sm:p-4">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
					<div className="flex items-center gap-3 min-w-0 flex-1">
						<div className="relative shrink-0">
							<img
								src={account.avatar || "https://placehold.co/48x48"}
								alt={account.handle}
								className="w-11 h-11 rounded-full object-cover bg-muted"
							/>
							<PlatformMark
								platform={account.platform}
								size="xs"
								className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white"
							/>
						</div>
						<div className="min-w-0">
							<div className="flex items-center gap-2 flex-wrap">
								<h3 className="font-semibold text-base text-foreground truncate">
									{account.displayName || account.handle}
								</h3>
								{/* The position is the verdict; it goes where the eye lands first. */}
								{s?.position && (
									<span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold', meta.chip)} title={meta.meaning}>
										<span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
										{meta.label}
									</span>
								)}
								{account.isVerified && <Badge variant="outline" size="sm">Verified</Badge>}
								{account.status && account.status !== 'active' && <Badge variant="outline" size="sm">{account.status}</Badge>}
							</div>
							<p className="text-muted-foreground text-sm truncate">@{account.handle}</p>
						</div>
					</div>

					<div className="flex flex-wrap items-center gap-2 lg:shrink-0">
						<Button asChild variant="outline" size="sm"><Link to={`${PATHS.HEARTBEAT_NOTES}?account=${encodeURIComponent(account._id)}`}>Account notes</Link></Button>
						<Button variant="outline" size="sm" onClick={() => navigate(buildRoute(ROUTE_PATTERNS.HEARTBEAT_ACCOUNT, { accountId: account._id }))}>
							Heartbeat
						</Button>
						<Button size="sm" onClick={handleScanAccount}>Scan</Button>
						<Dropdown trigger={<Button variant="outline" size="sm" aria-label={`More actions for @${account.handle}`}>More</Button>}>
							<DropdownItem onClick={handleRefresh} disabled={isAccountRefreshing}>{isAccountRefreshing ? <Spinner size="sm" /> : 'Refresh profile'}</DropdownItem>
							{canDeleteClientData(user) && <>
								<DropdownSeparator />
								<DropdownItem className="text-destructive" disabled={pendingId === account._id} onClick={() => beginDeletion('account', account, onRefreshComplete)}>
									{pendingId === account._id ? 'Checking deletion...' : 'Delete account'}
								</DropdownItem>
							</>}
						</Dropdown>
					</div>
				</div>

				{/* Each axis under the name of what it measures. */}
				<div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
					<div>
						<div className="text-xs text-muted-foreground">Followers</div>
						<div className="font-semibold text-foreground tabular-nums">{formatNumber(account.followerCount)}</div>
					</div>
					<div>
						<div className="text-xs text-muted-foreground" title="What they post could cost them with sponsors and platforms">Exposure</div>
						{band ? (
							<span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', band.badge)}>{riskLabel(account.risk)}</span>
						) : (
							<div className="text-sm text-muted-foreground">Not scored</div>
						)}
					</div>
					<div>
						<div className="text-xs text-muted-foreground" title="How the people replying feel about them">Standing</div>
						{positioned > 0 ? (
							<div className="text-sm tabular-nums">
								<span className={cn('font-medium', STANCE_META.for.text)}>{withPct}% with</span>
								<span className="text-muted-foreground"> · </span>
								<span className={cn('font-medium', STANCE_META.against.text)}>{againstPct}% against</span>
							</div>
						) : (
							<div className="text-sm text-muted-foreground">No pulse yet</div>
						)}
					</div>
					<div>
						<div className="text-xs text-muted-foreground">Last scan</div>
						<div className={`text-sm ${isStale ? 'font-medium text-amber-600' : 'text-foreground'}`}>
							{formatDate(account.lastScan)}
							{isStale && staleDays !== null && <span className="ml-1 text-xs">· {staleDays}d ago</span>}
						</div>
					</div>
				</div>

				{/* The saved read, two lines by default. */}
				{(insight || s?.reason) && (
					<div className="mt-3 border-t border-border pt-3">
						<p className={cn('text-sm leading-relaxed', insight ? 'text-foreground' : 'text-muted-foreground', !expanded && 'line-clamp-2', s?.insight?.stale && 'text-muted-foreground')}>
							{insight || s.reason}
						</p>
						<div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
							{(insight || s?.reason || '').length > 140 && (
								<button type="button" onClick={() => setExpanded((v) => !v)} className="font-medium text-brand-600 hover:underline">
									{expanded ? 'Less' : 'More'}
								</button>
							)}
							{s?.insight?.stale && <span>bringing this up to date…</span>}
							{s?.computedAt && <span>read {relativeTime(s.computedAt)}</span>}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
